import React from 'react';
import glamorous from 'glamorous';

import { Tree } from '@blueprintjs/core';
import { map, includes, startsWith } from 'lodash';

import { MENU_ENTRIES } from './constants';

const StyledTree = glamorous(Tree)({
  '& .pt-tree-node-content': {
    height: 'auto',
  },
  '& .pt-tree-node-label': {
    overflow: 'auto',
    whiteSpace: 'unset',
    wordWrap: 'break-word',
    marginTop: 2,
    marginBottom: 2,
  },
  '& .pt-tree-node': {
    marginBottom: '7px',
  },
  '& .pt-tree-node-content-1': {
    paddingLeft: 5,
  },
  '& .pt-tree-node-content-2': {
    paddingLeft: 15,
  },
});

const getTreeContents = (querySection = 'scope', expandedNodeIds) => {
  const items = [
    {
      id: MENU_ENTRIES.scope,
      label: 'Scope of this guide',
    },
    {
      id: MENU_ENTRIES.keyTermsUsed,
      label: 'Key terms used in the guide'
    },
    {
      id: MENU_ENTRIES.prerequisites,
      label: 'Prerequisites',
    },
    {
      id: MENU_ENTRIES.apiKey,
      label: 'API key',
      childNodes: [
        {
          id: MENU_ENTRIES.apiKeyGettingAccess,
          label: 'Getting access to an API key',
        }
      ],
    },
    {
      id: MENU_ENTRIES.overviewOfDataUploadingProcess,
      label: 'Overview of recall data uploading process',
    },
    {
      id: MENU_ENTRIES.creatingRecallInXmlFormat,
      label: 'STEP 1: Creating a recall in XML format',
      childNodes: [
        {
          id: MENU_ENTRIES.creatingRecallInXmlFormatExamples,
          label: 'XML examples',
          childNodes: [
            {
              id: MENU_ENTRIES.creatingRecallInXmlFormatExamplesNewRecall,
              label: 'Upload a new recall',
            },
            {
              id: MENU_ENTRIES.creatingRecallInXmlFormatExamplesUpdateRecall,
              label: 'Update an existing recall',
            },
            {
              id: MENU_ENTRIES.creatingRecallInXmlFormatExamplesDeleteRecall,
              label: 'Deleting an existing recall',
            },
          ],
        },
        {
          id: MENU_ENTRIES.creatingRecallInXmlFormatEmptyStructures,
          label: 'Empty XML structures',
          childNodes: [
            {
              id: MENU_ENTRIES.creatingRecallInXmlFormatEmptyStructuresNewRecall,
              label: 'Upload a new recall',
            },
            {
              id: MENU_ENTRIES.creatingRecallInXmlFormatEmptyStructuresUpdateRecall,
              label: 'Update an existing recall',
            },
            {
              id: MENU_ENTRIES.creatingRecallInXmlFormatEmptyStructuresDeleteRecall,
              label: 'Deleting an existing recall',
            },
          ],
        },
      ]
    },
    {
      id: MENU_ENTRIES.xmlValidation,
      label: 'STEP 2: Validating your XML file',
      childNodes: [
        {
          id: MENU_ENTRIES.xmlValidationFiles,
          label: 'Validation files',
        },
        {
          id: MENU_ENTRIES.xmlValidationSteps,
          label: 'Validation steps',
        }
      ],
    },
    {
      id: MENU_ENTRIES.ensureXmlAndImagesInOneFolder,
      label: 'STEPS 3 and 4: Ensuring all XML and image files are in one folder',
    },
    {
      id: MENU_ENTRIES.creatingZipFile,
      label: 'STEP 5: Creating a ZIP file',
      childNodes: [
        {
          id: MENU_ENTRIES.creatingZipFileZipCompression,
          label: 'Compressing your files',
        },
      ],
    },
    {
      id: MENU_ENTRIES.uploadingZipToPortal,
      label: 'STEP 6: Uploading the zip file to the portal',
      childNodes: [
        {
          id: MENU_ENTRIES.uploadingZipToPortalManualProcess,
          label: 'Manual upload process',
        }
      ],
    },
    {
      id: MENU_ENTRIES.apiTechnicalDetails,
      label: 'API technical details for automated uploads',
    },
    {
      id: MENU_ENTRIES.htmlEncoding,
      label: 'HTML encoding',
    },
    {
      id: MENU_ENTRIES.fieldsDescription,
      label: 'Field descriptions',
      childNodes: [
        {
          id: MENU_ENTRIES.fieldsDescriptionRecall,
          label: 'Field [recall]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionEditType,
          label: 'Field [edit_type]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionId,
          label: 'Field [id]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionCountryId,
          label: 'Field [country_id]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionManufacturer,
          label: 'Field [manufacturer]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionManufacturerCountryId,
          label: 'Field [manufacturer_country_id]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionProductType,
          label: 'Field [product_type]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionProductName,
          label: 'Field [product_name]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionProductDesc,
          label: 'Field [product_desc]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionProductCode,
          label: 'Field [product_code]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionProductModelNo,
          label: 'Field [product_model_no]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionHazard,
          label: 'Field [hazard]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionInjuries,
          label: 'Field [injuries]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionAction,
          label: 'Field [action]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionUnits,
          label: 'Field [units]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionURL,
          label: 'Field [URL]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionDate,
          label: 'Field [date]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionDistributorName,
          label: 'Field [distributor_name]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionDistributorWebsite,
          label: 'Field [distributor_website]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionGTIN,
          label: 'Field [GTIN]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionGTINProductType,
          label: 'Field [GTIN_product_type]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionGTINDesc,
          label: 'Field [GTIN_desc]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionHTS,
          label: 'Field [HTS]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionHTSProductType,
          label: 'Field [HTS_product_type]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionHTSDesc,
          label: 'Field [HTS_desc]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionImage,
          label: 'Field [image]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionManufacturerWebsite,
          label: 'Field [manufacturer_website]',
        },
        {
          id: MENU_ENTRIES.fieldsDescriptionSeriousRisk,
          label: 'Field [serious_risk]',
        },
      ],
    },
    {
      id: MENU_ENTRIES.productCodes,
      label: 'Complex field: Product codes',
      childNodes: [
        {
          id: MENU_ENTRIES.productCodesDefinition,
          label: 'Definition',
        },
        {
          id: MENU_ENTRIES.productCodesValues,
          label: 'Values',
        },
        {
          id: MENU_ENTRIES.productCodesXmlStructure,
          label: 'Structure of the codes in the XML file describing the recall',
        },
        {
          id: MENU_ENTRIES.productCodesFieldProductCode,
          label: 'Field [product_code]',
        },
        {
          id: MENU_ENTRIES.productCodesSubFieldPublication,
          label: 'Subfield [publication]',
        },
        {
          id: MENU_ENTRIES.productCodesSubFieldSegment,
          label: 'Subfield [segment]',
        },
        {
          id: MENU_ENTRIES.productCodesSubFieldFamily,
          label: 'Subfield [family]',
        },
        {
          id: MENU_ENTRIES.productCodesSubFieldClass,
          label: 'Subfield [class]',
        },
        {
          id: MENU_ENTRIES.productCodesSubFieldBrick,
          label: 'Subfield [brick]',
        },
        {
          id: MENU_ENTRIES.productCodesSubFieldAttrs,
          label: 'Subfields [attribute][type][value]',
        },
      ],
    },
    {
      id: MENU_ENTRIES.productCodesXmlExamples,
      label: 'Examples of product codes in XML',
    },
    {
      id: MENU_ENTRIES.gpcMoreInformation,
      label: 'More information on GPC codes',
    },
    {
      id: MENU_ENTRIES.complexFieldImages,
      label: 'Complex field: Images',
      childNodes: [
        {
          id: MENU_ENTRIES.complexFieldImagesFilename,
          label: 'Subfield [filename]',
        },
        {
          id: MENU_ENTRIES.complexFieldImagesMediaType,
          label: 'Subfield [media_type]',
        },
        {
          id: MENU_ENTRIES.complexFieldImagesAltText,
          label: 'Subfield [alt_text]',
        },
      ],
    },
  ];

  const isExpanded = (nodeId) => {
    if (includes(expandedNodeIds, nodeId)) return true;
    return querySection.indexOf('.') > -1 && startsWith(querySection, nodeId);
  };

  const mapChildItems = (item => {
    if (item.childNodes) {
      item.isExpanded = isExpanded(item.id);
      item.childNodes = map(item.childNodes, (childNode, cIdx) => {
        const _childItem = {
          ...childNode,
          path: [...item.path, cIdx],
          isSelected: childNode.id === querySection,
        };
        if(_childItem.childNodes) {
          mapChildItems(_childItem);
        }
        return _childItem;
      });
    }
  });

  return map(items, (item, pIdx) => {
    const _item = {
      ...item,
      path: [pIdx],
      isSelected: item.id === querySection,
    };

    mapChildItems(_item);

    return _item;
  });
};

const DocMenu = ({ currentSection, onSectionChange, onNodeExpandToggle, expandedNodeIds }) => {
  const treeContent = getTreeContents(currentSection, expandedNodeIds);
  return (
    <StyledTree
      contents={treeContent}
      onNodeClick={node => onSectionChange(node.id)}
      onNodeCollapse={onNodeExpandToggle}
      onNodeExpand={onNodeExpandToggle}
    />
  )
};

export default DocMenu;


// WEBPACK FOOTER //
// ./src/views/static/import-documentation/doc-menu.js